import {
  Bot,
  BookOpen,
  Brain,
  BrainCircuit,
  GitBranch,
  Building2,
  CalendarCheck2,
  CalendarDays,
  CalendarX2,
  CircleDollarSign,
  ClipboardList,
  Clock,
  Files,
  Handshake,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  MessageSquare,
  PlugZap,
  Receipt,
  Send,
  Settings,
  Stethoscope,
  Tag,
  Target,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react';

import MetaIcon from '../components/icons/MetaIcon.tsx';
import type { ConnectionStatus } from '../hooks/useWhatsAppStatus.ts';

export type MenuPermission = string;

export interface SidebarSubItem {
  path: string;
  label: string;
  icon?: LucideIcon;
  isNew?: boolean;
  isBeta?: boolean;
  permission?: MenuPermission;
  masterOnly?: boolean;
}

export interface SidebarMenuItem {
  path: string;
  icon: LucideIcon;
  label: string;
  badge?: string;
  status?: ConnectionStatus;
  isNew?: boolean;
  isBeta?: boolean;
  subItems?: SidebarSubItem[];
  group?: string;
  permission?: MenuPermission;
  permissionAliases?: MenuPermission[];
  masterOnly?: boolean;
}

export interface SidebarMenuSection {
  label: string;
  icon: LucideIcon;
  items: SidebarMenuItem[];
}

export interface MenuPermissionCatalogItem {
  key: MenuPermission;
  label: string;
  section: string;
  type: 'menu' | 'submenu';
  parentKey?: MenuPermission;
  path: string;
  icon?: LucideIcon;
  isNew?: boolean;
  isBeta?: boolean;
  masterOnly?: boolean;
}

const SIDEBAR_MENU_SECTIONS: SidebarMenuSection[] = [
  {
    label: 'Relatórios',
    icon: LineChart,
    items: [
      {
        path: '/dashboard',
        icon: LayoutDashboard,
        label: 'Visão geral',
        permission: 'dashboard',
      },
      {
        path: '/dashboard/ads',
        icon: MetaIcon,
        label: 'Meta Ads',
        permission: 'prompt.ads',
      },
      {
        path: '/dashboard/metrics',
        icon: LineChart,
        label: 'Métricas',
        permission: 'prompt.metrics',
      },
      {
        path: '/dashboard/metas',
        icon: Target,
        label: 'Metas',
        group: 'Metas',
        permission: 'dashboard.metas',
      },
    ],
  },
  {
    label: 'Comercial',
    icon: CircleDollarSign,
    items: [
      {
        path: '/crm',
        icon: TrendingUp,
        label: 'CRM',
        permission: 'crm',
        permissionAliases: ['crm.pipeline'],
      },
      {
        path: '/campanhas',
        icon: Target,
        label: 'Campanhas',
        permission: 'crm.campanhas',
        isBeta: true,
      },
      {
        path: '/contacts',
        icon: Users,
        label: 'Contatos',
        group: 'Listas',
        permission: 'contacts',
      },
      {
        path: '/tags',
        icon: Tag,
        label: 'Tags',
        group: 'Listas',
        permission: 'tags',
      },
    ],
  },
  {
    label: 'Chat',
    icon: MessageSquare,
    items: [
      {
        path: '/chat',
        icon: MessageSquare,
        label: 'Chat',
        permission: 'chat',
      },
    ],
  },
  {
    label: 'Agentes',
    icon: BrainCircuit,
    items: [
      {
        path: '/prompt',
        icon: Brain,
        label: 'Agente IA',
        permission: 'prompt',
      },
      {
        path: '/workflows',
        icon: GitBranch,
        label: 'Fluxos',
        permission: 'prompt',
      },
      {
        path: '/follow-up',
        icon: Send,
        label: 'Follow-up',
        permission: 'follow-up',
      },
      {
        path: '/follow-up/confirmacao',
        icon: CalendarCheck2,
        label: 'Confirmação de Consulta',
        permission: 'follow-up.confirmacao',
      },
      {
        path: '/follow-up/noshow',
        icon: CalendarX2,
        label: 'No-Show',
        permission: 'follow-up.noshow',
      },
      {
        path: '/follow-up/pos-consulta',
        icon: Stethoscope,
        label: 'Pós-Consulta',
        permission: 'follow-up.pos-consulta',
      },
      {
        path: '/follow-up/pos-venda',
        icon: Handshake,
        label: 'Pós-Venda',
        permission: 'follow-up.pos-venda',
      },
      {
        path: '/prompt/clock-ai',
        icon: Clock,
        label: 'Horário de Resposta',
        group: 'Configurações',
        permission: 'prompt.clock-ai',
      },
      {
        path: '/clinic/controle-fluxos',
        icon: Bot,
        label: 'Controle de Agentes',
        group: 'Configurações',
        permission: 'clinic.controle-fluxos',
      },
    ],
  },
  {
    label: 'Configurações',
    icon: Settings,
    items: [
      {
        path: '/canais',
        icon: LayoutGrid,
        label: 'Canais',
        permission: 'whatsapp',
        permissionAliases: ['whatsapp.conexao'],
      },
      {
        path: '/prompt/templates',
        icon: Files,
        label: 'Biblioteca de templates',
        permission: 'prompt.templates',
      },
      {
        path: '/prompt/agenda',
        icon: CalendarDays,
        label: 'Agenda',
        group: 'Integrações',
        permission: 'prompt.agenda',
      },
      {
        path: '/prompt/form-webhook',
        icon: ClipboardList,
        label: 'Formulários',
        group: 'Integrações',
        permission: 'prompt.agenda',
      },
      {
        path: '/configuracoes/conexoes-mcp',
        icon: PlugZap,
        label: 'MCP e conexões de IA',
        group: 'Integrações',
        permission: 'mcp',
      },
      {
        path: '/configuracoes/crm',
        icon: Settings,
        label: 'Motivos de perda',
        group: 'CRM',
        masterOnly: true,
      },
      {
        path: '/clinic',
        icon: Building2,
        label: 'Configuração da Clínica',
        group: 'Clínica',
        permission: 'clinic',
      },
      {
        path: '/reports',
        icon: Receipt,
        label: 'Plano e Fatura',
        group: 'Clínica',
        masterOnly: true,
      },
    ],
  },
];

const MOBILE_MENU_PATHS = ['/dashboard', '/crm', '/chat', '/canais'];

function withWhatsAppStatus(item: SidebarMenuItem, status?: ConnectionStatus): SidebarMenuItem {
  // O estado de um número WhatsApp não representa o conjunto de canais.
  if (item.path === '/canais') return item;
  if (item.path !== '/whatsapp') {
    return item;
  }
  return { ...item, status };
}

export function buildSidebarMenuSections(whatsappStatus?: ConnectionStatus, isPlatformAdmin = false): SidebarMenuSection[] {
  return SIDEBAR_MENU_SECTIONS.map(section => ({
    ...section,
    items: [
      ...section.items.map(item => withWhatsAppStatus(item, whatsappStatus)),
      ...(isPlatformAdmin && section.label === 'Configurações' ? [{
        path: '/admin/registrations',
        icon: ClipboardList,
        label: 'Aprovação de cadastros',
        group: 'Administração',
      }] : []),
    ],
  }));
}

export function buildMobileMenuItems(whatsappStatus?: ConnectionStatus): SidebarMenuItem[] {
  const itemsByPath = new Map<string, SidebarMenuItem>();

  SIDEBAR_MENU_SECTIONS.forEach(section => {
    section.items.forEach(item => {
      itemsByPath.set(item.path, withWhatsAppStatus(item, whatsappStatus));
    });
  });

  return MOBILE_MENU_PATHS
    .map(path => itemsByPath.get(path))
    .filter((item): item is SidebarMenuItem => Boolean(item));
}

export function getMenuPermissionCatalog(): MenuPermissionCatalogItem[] {
  const catalog = new Map<MenuPermission, MenuPermissionCatalogItem>();

  const addPermission = (item: MenuPermissionCatalogItem) => {
    const { key } = item;
    if (!key || catalog.has(key)) {
      return;
    }
    catalog.set(key, item);
  };

  SIDEBAR_MENU_SECTIONS.forEach(section => {
    section.items.forEach(item => {
      if (!item.permission) {
        return;
      }
      addPermission({
        key: item.permission,
        label: item.label,
        section: section.label,
        type: 'menu',
        path: item.path,
        icon: item.icon,
        masterOnly: item.masterOnly,
      });
    });
  });

  SIDEBAR_MENU_SECTIONS.forEach(section => {
    section.items.forEach(item => {
      item.subItems?.forEach(subItem => {
        if (!subItem.permission) {
          return;
        }
        addPermission({
          key: subItem.permission,
          label: subItem.label,
          section: section.label,
          type: 'submenu',
          parentKey: item.permission,
          path: subItem.path,
          icon: subItem.icon,
          isNew: subItem.isNew,
          isBeta: subItem.isBeta,
          masterOnly: subItem.masterOnly,
        });
      });
    });
  });

  return Array.from(catalog.values());
}

export function getMenuPermissionTree(): Array<MenuPermissionCatalogItem & { subItems: MenuPermissionCatalogItem[] }> {
  const catalog = getMenuPermissionCatalog();
  const subItemsByParent = new Map<MenuPermission, MenuPermissionCatalogItem[]>();

  catalog
    .filter(item => item.type === 'submenu' && item.parentKey)
    .forEach(item => {
      const parentItems = subItemsByParent.get(item.parentKey as MenuPermission) || [];
      parentItems.push(item);
      subItemsByParent.set(item.parentKey as MenuPermission, parentItems);
    });

  return catalog
    .filter(item => item.type === 'menu')
    .map(item => ({
      ...item,
      subItems: subItemsByParent.get(item.key) || [],
    }));
}
